import * as React from 'react'
import styled from 'styled-components'
import InfiniteScroll from 'react-infinite-scroller'
import { LeaderboardTime } from '.'
import { useInfiniteLoadIndexer } from '../../hooks/infiniteLoadIndexer'
import { COLLECTOR_LEADERBOARD_QUERY } from '../../graphql/graphql-queries'
import { SmallSpinnerContainer, SmallSpinner } from '../spinner'
import { CollectorLeaderboard, OrderBy, Query_Root } from '../../graphql/indexer-graphql-types'
import { CollectorLeaderboardUser } from '../../interfaces/User'
import { fbEnrichUsersFromIds } from '../../data/FirebaseEnrich'
import TraderHeader from './headers/TraderHeader'
import TraderRow from './rows/TraderRow'
import TraderSkeletonItem from './rows/TraderSkeletonItem'
import { ownerComparer } from '../../utils/comparer'

const itemsContainerStyle = `
  display: flex;
  flex-wrap: wrap;
  position: relative;
  z-index: 0;
  width: 100%;

  // large desktop
  @media (min-width: 82em) {
    max-width: 1800px;
  }
`

const StyledSkeletonContainer = styled.div`
  ${itemsContainerStyle}
`

const StyledInfiniteScroll = styled(InfiniteScroll)`
  ${itemsContainerStyle}
`

const StyledTable = styled.table`
  width: 100%;
  border: none;
  border-collapse: separate;
  border-spacing: 0 15px;
  // large desktop
  @media (min-width: 82em) {
    max-width: 1800px;
  }
`

interface Props {
  time: LeaderboardTime
  currentIndex: number
  index: number
  updateHeight: () => void
}

const parseItems = (data: Query_Root | undefined): CollectorLeaderboard[] => {
  if (!data) {
    return []
  }
  return data.collectorLeaderboard
    ?.filter(Boolean)
    ?.filter(({ owner }) => !!owner) as CollectorLeaderboard[]
}

const enrichItems = async (
  leaderboardItems: CollectorLeaderboard[]
): Promise<CollectorLeaderboardUser[]> => {
  const userIDs = leaderboardItems.map(({ owner }) => owner) as string[] | []
  if (!userIDs.length) {
    return []
  }
  const collectors = await fbEnrichUsersFromIds(userIDs)
  return userIDs.map((id) => {
    const collector = collectors.find((collector) => collector.address === id)
    const leaderboardData = leaderboardItems.find((data) => data.owner === id)

    return {
      id,
      user: collector,
      leaderboardInfo: leaderboardData,
    }
  })
}

export enum TraderSelectedFilter {
  FLIPPED,
  EARNED,
}

const TrendingTraders = ({ currentIndex, index, updateHeight, time }: Props): JSX.Element => {
  const [selected, setSelected] = React.useState<TraderSelectedFilter>(TraderSelectedFilter.EARNED)

  const {
    rawData: originalItems,
    data: items,
    fetchNext,
    isLoading,
    hasNext,
    refetch,
  } = useInfiniteLoadIndexer<CollectorLeaderboardUser, string>({
    id: '',
    query: COLLECTOR_LEADERBOARD_QUERY,
    enrichItems,
    parseItems,
    comparer: ownerComparer,
    page_size: 10,
    refetchMarker: 'trendingTraders',
    variables: {
      orderBy:
        time === LeaderboardTime.ALL
          ? selected === TraderSelectedFilter.EARNED
            ? [{ allReturns: OrderBy.Desc }]
            : [{ allFlipped: OrderBy.Desc }]
          : time === LeaderboardTime.MONTH
          ? selected === TraderSelectedFilter.EARNED
            ? [{ monthReturns: OrderBy.Desc }]
            : [{ monthFlipped: OrderBy.Desc }]
          : selected === TraderSelectedFilter.EARNED
          ? [{ weekReturns: OrderBy.Desc }]
          : [{ weekFlipped: OrderBy.Desc }],
    },
  })

  React.useEffect(() => {
    if (index === currentIndex) {
      refetch()
    }
  }, [currentIndex, index, time, selected])

  React.useEffect(() => {
    if (index === currentIndex && !isLoading) {
      updateHeight()
    }
  }, [items])

  if ((originalItems.length === 0 && hasNext) || isLoading) {
    return (
      <StyledSkeletonContainer>
        <StyledTable>
          <TraderHeader selected={selected} setSelected={setSelected} />
          <tbody>
            {[...Array(10).keys()].map((index) => (
              <TraderSkeletonItem rank={index + 1} key={`skeleton-item-${index}`} />
            ))}
          </tbody>
        </StyledTable>
      </StyledSkeletonContainer>
    )
  }

  if (items.length === 0) {
    return <></>
  }

  return (
    <StyledInfiniteScroll
      loadMore={fetchNext}
      hasMore={hasNext}
      loader={
        <SmallSpinnerContainer>
          <SmallSpinner />
        </SmallSpinnerContainer>
      }
      useWindow
    >
      <StyledTable>
        <TraderHeader selected={selected} setSelected={setSelected} />
        <tbody>
          {items.map(
            (item: CollectorLeaderboardUser, index: number): JSX.Element => (
              <TraderRow
                time={time}
                filter={selected}
                rank={index + 1}
                {...item}
                key={`list-item-${index}`}
              />
            )
          )}
        </tbody>
      </StyledTable>
    </StyledInfiniteScroll>
  )
}

export default TrendingTraders
